import { useCallback, useContext, useEffect, useRef } from 'react'
import * as React from 'react'
import { INTL_DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import TextLink from 'LuxKit/TextLink'
import Caption from 'LuxKit/Typography/Caption'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import { initialiseBnblOfferModal, updateBnblGifting } from 'actions/BnblModalActions'
import { getBnblPackageViews } from 'selectors/offerPage/offerPageSelectors'
import { useAppDispatch } from 'hooks/reduxHooks'
import useToggle from 'hooks/useToggle'
import BnblModal from './BnblModal/BnblModal'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineGiftIcon from 'LuxKit/Icons/line/LineGiftIcon'
import getDatasetFromEvent from 'lib/analytics/getDatasetFromEvent'
import OfferPageStateTrackingContext from 'contexts/OfferPage/offerPageStateTrackingContext'
import Group from 'components/utils/Group'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import { getBnblOccupancies, getBnblPackageViewByNumberOfRooms } from './BnblModal/utils'
import { EmptyObject } from 'lib/object/objectUtils'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import LineClockIcon from 'LuxKit/Icons/line/LineClockIcon'

const MenuLink = styled(TextLink)`
  && {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: ${rem(48)};
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
    text-align: start;
    font-size: ${rem(14)};

    > svg {
      width: ${rem(24)};
      height: ${rem(24)};
      flex: none;
    }
  }
`

interface Props {
  offer: App.HotelOffer
  baseBnblPackageViews: Array<App.OfferPackageOptionView>
  packageView?: App.OfferPackageOptionView
  rooms: Array<App.Occupants>
  direction?: 'vertical' | 'horizontal'
  desktopDirection?: 'vertical' | 'horizontal'
  presentation?: 'menu'
  disabled?: boolean
  onOpenModal?: (e: React.MouseEvent<HTMLAnchorElement>) => boolean | void
  /** A containing flow owns the sheet; resume preserves its previously edited BNBL selection. */
  modalPresentation?: {
    kind: 'embedded'
    resume: boolean
    onPrepared: () => void
  }
  /** Option D standalone modal: reject checkout when the originating account or quote changed. */
  onBeforePurchase?: () => boolean
  /** Changing this closes an already-open standalone modal. Control omits it. */
  purchaseSessionKey?: string
  onPurchaseSessionChange?: (open: boolean) => void
  /** Lets the parent close the modal after the desktop menu has stopped rendering updates. */
  onRegisterClose?: (close: (() => void) | undefined) => void
  charterBundleRoute?: App.CharterBundleRoute
}

function OfferPageBNBLcta(props: Props) {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const intlDateFormatter = useIntlDateFormatter()
  const [bnblModalOpen, _, openBnblModal, closeBnblModal] = useToggle()
  const {
    offer,
    baseBnblPackageViews,
    packageView = EmptyObject,
    rooms,
    direction = 'vertical',
    desktopDirection = direction,
    presentation,
    onOpenModal,
    disabled = false,
    modalPresentation,
    onBeforePurchase,
    purchaseSessionKey,
    onPurchaseSessionChange,
    onRegisterClose,
  } = props
  const purchaseSessionKeyRef = useRef(purchaseSessionKey)
  useEffect(() => {
    if (purchaseSessionKeyRef.current === purchaseSessionKey) return
    purchaseSessionKeyRef.current = purchaseSessionKey
    closeBnblModal()
  }, [closeBnblModal, purchaseSessionKey])
  useEffect(() => {
    onPurchaseSessionChange?.(bnblModalOpen)
  }, [bnblModalOpen, onPurchaseSessionChange])
  useEffect(() => {
    onRegisterClose?.(closeBnblModal)
    return () => onRegisterClose?.(undefined)
  }, [closeBnblModal, onRegisterClose])
  const isMenu = presentation === 'menu'
  const ActionLink = isMenu ? MenuLink : TextLink
  const buyNowBookLaterLabel = isMenu ? intl.formatMessage({
    id: 'RuRqHhzSSlaXhwPrNau1',
    defaultMessage: 'Buy now, book dates later',
    description: 'Flash Option D menu action for buying a hotel package now and choosing dates later',
  }) : intl.formatMessage({
    id: 'xw0aZVCaHQ6DODWcupr4',
    defaultMessage: 'Buy now, choose dates later',
    description: 'Hyperlink text for buying now and choosing dates later',
  })
  const giftLabel = isMenu ? intl.formatMessage({
    id: '1HqFV8Su-CRcbA7Vh9mL',
    defaultMessage: 'Gift this package',
    description: 'Flash Option D menu action for gifting the currently selected hotel package',
  }) : intl.formatMessage({
    id: 'D5ZD2rVGtO1kiiV1Txrj',
    defaultMessage: 'Give as gift',
    description: 'Link text for giving the offer as a gift on the gift card page',
  })

  const isSevenDaysBeforeTravelToDate = moment().add(1, 'w').isBefore(offer.travelToDate)
  const isMemberOnlyOffer = offer.luxPlus.access === 'memberOnly'

  const { trackEvent } = useContext(OfferPageStateTrackingContext)

  const openAndInitaliseBnblModal = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (disabled) {
      e.preventDefault()
      return
    }
    if (!modalPresentation) {
      trackEvent('BUY_NOW_CHOOSE_DATES_LATER_PRESSED')
      e.preventDefault()
      openBnblModal()

      if (onOpenModal) {
        onOpenModal(e)
        return
      }

      const isGift = getDatasetFromEvent(e, 'bnbltype') === 'gift'
      const numberOfRooms = rooms?.length ?? 1
      const bnblPackageView = baseBnblPackageViews.find(baseBnblPackageView => baseBnblPackageView.package.uniqueKey === packageView?.package.uniqueKey)
      if (!bnblPackageView) return

      const packages = [{
        packageView: getBnblPackageViewByNumberOfRooms(bnblPackageView, numberOfRooms),
        numberOfRooms,
        occupancies: getBnblOccupancies(rooms),
      }]
      dispatch(initialiseBnblOfferModal(packages, offer.id, isGift))
      return
    }
    trackEvent('BUY_NOW_CHOOSE_DATES_LATER_PRESSED')
    e.preventDefault()
    const isGift = getDatasetFromEvent(e, 'bnbltype') === 'gift'
    if (modalPresentation?.resume) {
      dispatch(updateBnblGifting(offer.id, isGift))
    } else if (onOpenModal) {
      if (onOpenModal(e) === false) return
    } else {
      const numberOfRooms = rooms?.length ?? 1
      const bnblPackageView = baseBnblPackageViews.find(baseBnblPackageView => baseBnblPackageView.package.uniqueKey === packageView?.package.uniqueKey)
      if (!bnblPackageView) return

      const packages = [{
        packageView: getBnblPackageViewByNumberOfRooms(bnblPackageView, numberOfRooms),
        numberOfRooms,
        occupancies: getBnblOccupancies(rooms),
      }]
      dispatch(initialiseBnblOfferModal(packages, offer.id, isGift))
    }
    if (modalPresentation) modalPresentation.onPrepared()
    else openBnblModal()
  }, [baseBnblPackageViews, disabled, dispatch, offer.id, openBnblModal, packageView, rooms, trackEvent, onOpenModal, modalPresentation])

  if (props.charterBundleRoute) return null

  return (<>
    <Group
      wrap={direction === 'horizontal' ? 'wrap' : 'nowrap'}
      direction={direction}
      gap={isMenu ? 0 : direction === 'horizontal' ? '4 16' : 4}
      desktopWrap={desktopDirection === 'horizontal' ? 'wrap' : 'nowrap'}
      desktopDirection={desktopDirection}
      desktopGap={isMenu ? 0 : desktopDirection === 'horizontal' ? '4 16' : 4}
    >
      <ActionLink
        disabled={disabled}
        size={isMenu ? 'small' : 'caption'}
        weight={isMenu ? 'regular' : 'bold'}
        underline={!isMenu}
        onClick={openAndInitaliseBnblModal}
        data-testid="buyNowChooseDatesLater"
        startIcon={isMenu ? <LineClockIcon /> : <LineCalendarIcon />}
      >
        {buyNowBookLaterLabel}
      </ActionLink>

      {isSevenDaysBeforeTravelToDate && !isMemberOnlyOffer && <ActionLink
        disabled={disabled}
        data-bookingtype="gift"
        size={isMenu ? 'small' : 'caption'}
        weight={isMenu ? 'regular' : 'bold'}
        underline={!isMenu}
        onClick={openAndInitaliseBnblModal}
        data-testid="sendAsGift"
        data-bnbltype="gift"
        startIcon={<LineGiftIcon />}
      >
        {giftLabel}
      </ActionLink>}
      {!isSevenDaysBeforeTravelToDate && offer.travelToDate && <Caption variant="medium">
        {intl.formatMessage(
          {
            id: '07JSbrk-7DZBGGTMiIZa',
            defaultMessage: 'Travel until {date}',
            description: 'Caption showing travel until date in the date selection or calendar picker',
          },
          {
            date: intlDateFormatter(offer.travelToDate, INTL_DMY_CASUAL_FORMAT),
          },
        )}
      </Caption>}
    </Group>
    {!modalPresentation && bnblModalOpen && <BnblModal closeBnblModal={closeBnblModal} isOpen={bnblModalOpen} offer={offer} onBeforePurchase={onBeforePurchase} />}
  </>)
}

export default withOfferPageState((state) => ({
  rooms: state.rooms,
  offer: state.offer,
  baseBnblPackageViews: getBnblPackageViews(state),
  charterBundleRoute: state.charterBundleRoute,
}), OfferPageBNBLcta)
